import { createRootRoute, createRoute, createRouter, Link, Navigate, Outlet, redirect, useNavigate, useRouteContext, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { delay, STAGGER, ThemeSwitch, ToolIcon, useHoverCard, useSidePicker, useTableLabels } from '@caios/ui';
import { Home } from './routes/home.tsx';
import { SignIn, VerifySecondFactor } from './routes/signin.tsx';
import { SecuritySetup } from './routes/security-setup.tsx';
import { Admin } from './routes/admin.tsx';
import { DELIVERY_TABS } from './delivery-nav.tsx';
import { Comms } from './routes/comms.tsx';
import { Media } from './routes/media.tsx';
import { AcceptInvite } from './routes/invite.tsx';
import { Security } from './routes/security.tsx';
import { Operations } from './routes/ops.tsx';
import { Support } from './routes/support.tsx';
import { Csat, HelpCentre, Portal } from './routes/help.tsx';
import { Scheduling } from './routes/scheduling.tsx';
import { ClientDetail, Clients } from './routes/clients.tsx';
import { CommandPalette } from './command-palette.tsx';
import { Onboarding, OnboardIntake, OnboardSecret } from './routes/onboarding.tsx';
import { Finance } from './routes/finance.tsx';
import { Leads } from './routes/leads.tsx';
import { Deals } from './routes/deals.tsx';
import { Calls } from './routes/calls.tsx';
import { Outreach } from './routes/outreach.tsx';
import { Relationships } from './routes/relationships.tsx';
import { GoalsPage } from './routes/goals.tsx';
import { Referrals } from './routes/referrals.tsx';
import { Connections } from './routes/connections.tsx';
import { Proofboard } from './routes/proofboard.tsx';
import { Reports } from './routes/reports.tsx';
import { Content } from './routes/content.tsx';
import { Search } from './routes/search.tsx';
import { Social, Ads } from './routes/social.tsx';
import { Results } from './routes/results.tsx';
import { Agents, Recommendations } from './routes/recommendations.tsx';
import { DesignLibrary } from './routes/design-library.tsx';
import { BookingPage, BookingType, ManageBooking, ScheduleRedirect } from './routes/booking-public.tsx';
import { RunningBar } from './components/timer.tsx';
import { ClientPortal } from './routes/portal.tsx';
import { Pocket } from './routes/pocket.tsx';
import { authClient, gate, rememberOrg, type Me } from './auth.ts';

/** The seven workspaces (plan §2.3). Routes exist for all; the rail shows the ones that are built. */
export const WORKSPACES = [
  { path: '/', label: 'Inbox' },
  { path: '/clients', label: 'Clients' },
  { path: '/onboarding', label: 'Onboarding' },
  { path: '/sales', label: 'Sales' },
  { path: '/delivery', label: 'Delivery' },
  { path: '/comms', label: 'Comms' },
  { path: '/ops', label: 'Operations' },
  { path: '/support', label: 'Support' },
  { path: '/scheduling', label: 'Scheduling' },
  { path: '/finance', label: 'Finance' },
  { path: '/admin', label: 'Admin' },
] as const;

/**
 * The rail: the old CAIOS shell's layout and look — icons only, the name and blurb in the hover card — (Sales → Marketing → Clients → Owner, in the order a client moves through the
 * business), one locked icon per old tool. Label = the 2.0 name. Harbor is one entry, as it was; its pages are listed
 * in their side panel (DELIVERY_TABS). Blurbs follow ICON-SPEC §6 (what it does for the person).
 */
type RailTo = '/' | '/clients' | '/onboarding' | '/sales' | '/sales/deals' | '/sales/calls' | '/sales/outreach' | '/sales/relationships' | '/goals' | '/finance/referrals' | '/delivery/reports' | '/comms' | '/comms/media' | '/finance' | '/ops' | '/scheduling' | '/support' | '/admin';
type RailItem = { to: RailTo; name: string; icon: string; blurb: string; roles?: string[]; search?: Record<string, string>; match?: readonly string[]; phone?: true };
const STAFF = ['owner', 'admin', 'manager'];
const RAIL: { label: string | null; items: RailItem[] }[] = [
  { label: null, items: [{ to: '/', name: 'Inbox', icon: 'owner-dashboard', phone: true, blurb: 'Everything waiting on you: assignments, mentions, bookings and tickets.' }] },
  { label: 'Sales', items: [
    { to: '/sales', name: 'Leads', icon: 'armada', blurb: 'Every brand’s own lead list: the businesses, the people there, and how to reach them.' },
    { to: '/sales/outreach', name: 'Outreach', icon: 'drydock', blurb: 'Cold email per brand: sequences, the approval queue, replies, mailboxes and domains.' },
    { to: '/sales/calls', name: 'Calls', icon: 'hailer', blurb: 'Call lists and the caller’s queue: one lead at a time, the script, and how it went.' },
    { to: '/sales/deals', name: 'Deals', icon: 'passage', blurb: 'Boards of deals per brand and client: stages, value, products and the next step.' },
  ] },
  { label: 'Marketing', items: [
    { to: '/delivery/reports', name: 'Delivery', icon: 'harbor', phone: true, match: DELIVERY_TABS.map((t) => t.to), blurb: 'Client delivery: recommendations to approve, reports, content, search, social, ads, monthly results, the agents, the proofboard and each client’s connections.' },
    { to: '/onboarding', name: 'Onboarding', icon: 'muster', blurb: 'Every new engagement from handoff to production: checklist, intake, access and credentials.' },
    { to: '/ops', name: 'Tasks', icon: 'manifest', phone: true, blurb: 'Plan and track the work for every client, with timers and saved views.' },
    { to: '/comms', name: 'Audience', icon: 'pennant', blurb: 'A brand’s subscribers, drips and broadcasts. The team drafts; a manager decides what goes out.' },
    { to: '/comms/media', name: 'Media', icon: 'masthead', blurb: 'Journalists and editors, kept current by what outlets publish: who joined, went quiet or moved.' },
    { to: '/sales/relationships', name: 'Relationships', icon: 'beacon', roles: STAFF, blurb: 'Warm contacts per brand, hottest first: why now, the evidence, and one message you validate and approve.' },
  ] },
  { label: 'Clients', items: [
    { to: '/clients', name: 'Clients', icon: 'register', phone: true, blurb: 'Every client, their terms and everything in flight for them. ⌘K jumps to one.' },
    { to: '/support', name: 'Support', icon: 'anchor', blurb: 'Client tickets from email, the portal and alerts, with replies and SLAs.' },
    { to: '/finance/referrals', name: 'Referrals', icon: 'envoy', blurb: 'Referral partners: who sent which client, what they’ve earned, and paying them.', roles: STAFF },
  ] },
  { label: 'Owner', items: [
    { to: '/finance', name: 'Money', icon: 'purser', blurb: 'Proposals, contracts and what came in — Stripe and bank transfers — and what’s due next.', roles: STAFF },
    { to: '/scheduling', name: 'Scheduling', icon: 'meetpoint', blurb: 'Booking pages, your availability and every call on the calendar.' },
    { to: '/goals', name: 'Goals', icon: 'helm', roles: ['owner', 'admin'], blurb: 'Each brand’s plan, objectives and KPIs against target — measured by CAIOS — and the weekly review.' },
    { to: '/admin', name: 'Monitoring', icon: 'fleetwatch', search: { tab: 'monitoring' }, roles: STAFF, blurb: 'Every site, service and scheduled job, with what is down and why.' },
  ] },
];

function Shell() {
  const { me } = useRouteContext({ from: '/_app' }) as { me: Me };
  // A client never sees the team's workspace: their own portal, in their provider's name.
  return me.role.startsWith('client') ? <ClientPortal me={me} /> : <StaffShell />;
}

function StaffShell() {
  const { me } = useRouteContext({ from: '/_app' }) as { me: Me };
  const navigate = useNavigate();
  const session = authClient.useSession();
  const card = useHoverCard();
  useTableLabels();
  useSidePicker();
  const [menu, setMenu] = useState<DOMRect | null>(null);
  const [more, setMore] = useState(false);   // phones: every tool, in a sheet above the bottom bar
  const path = useRouterState({ select: (r) => r.location.pathname });
  const count = useQuery({ queryKey: ['inbox-count'], queryFn: async () => (await (await fetch('/api/v1/inbox/count', { credentials: 'include' })).json()) as { open: number; unread: number }, refetchInterval: 20_000 });
  async function signOut() {
    await authClient.signOut();
    navigate({ to: '/signin' });
  }
  // Someone in several orgs (the owner of ARC, Live Echo and 6 Hands) switches here. The server checks the
  // membership; the page reloads so nothing from the previous org stays on screen or in a cache.
  const orgs = useQuery({ queryKey: ['my-orgs'], queryFn: async () => (await authClient.organization.list()).data ?? [] });
  async function switchOrg(id: string) {
    if (id === me.orgId) return;
    const r = await authClient.organization.setActive({ organizationId: id });
    if (r.error) return;
    rememberOrg(id);
    window.location.assign('/');
  }
  const name = session.data?.user.name ?? '';
  const initials = name.split(/\s+/).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '·';
  const photo = (session.data?.user as { image?: string | null } | undefined)?.image ?? null;
  const face = photo ? <img src={photo} alt="" /> : initials;
  let n = 0;
  const rail = RAIL.map((g) => ({ ...g, items: g.items.filter((it) => !it.roles || it.roles.includes(me.role)) })).filter((g) => g.items.length);
  return (
    <div className="cx-app cx-shell">
      <nav className="cx-rail" aria-label="Workspaces">
        <Link to="/" className="cx-rail-brand cx-rail-in" aria-label="CAIOS home"><img src="/caios.png" alt="" /></Link>
        <div className="cx-rail-scroll">
          {rail.map((g) => [
            g.label ? <div key={`l-${g.label}`} className="cx-rail-label cx-rail-group cx-rail-in" style={delay(++n, STAGGER.rail)}>{g.label}</div> : null,
            ...g.items.map((it) => (
              <Link key={it.name} to={it.to} search={it.search as never} className={`cx-rail-item cx-rail-in${it.phone ? ' cx-rail-item--phone' : ''}`} style={delay(++n, STAGGER.rail)} data-testid={`rail-${it.icon}`} aria-label={it.name} data-name={it.name}
                    {...(it.match ? { 'aria-current': it.match.some((m) => path.startsWith(m)) ? 'page' : undefined } : { activeProps: { 'aria-current': 'page' }, activeOptions: { exact: it.to === '/' || it.to === '/sales' || it.to === '/finance' } })}
                    onMouseEnter={(e) => card.show(e.currentTarget, it.name, it.blurb)} onMouseLeave={card.hide} onFocus={(e) => card.show(e.currentTarget, it.name, it.blurb)} onBlur={card.hide}>
                <ToolIcon name={it.icon} />
                {it.to === '/' && !!count.data?.unread && <span className="cx-rail-badge" data-testid="inbox-badge">{count.data.unread}</span>}
              </Link>
            )),
          ])}
          <button type="button" className="cx-rail-item cx-rail-more" aria-label="All tools" data-name="More" aria-expanded={more} data-testid="rail-more" onClick={() => setMore(!more)}>
            <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"><circle cx="5" cy="12" r="2" fill="currentColor" /><circle cx="12" cy="12" r="2" fill="currentColor" /><circle cx="19" cy="12" r="2" fill="currentColor" /></svg>
          </button>
        </div>
        <div className="cx-rail-foot cx-rail-in" style={delay(++n, STAGGER.rail)}>
          <button type="button" className="cx-avatar" aria-label="Account" title={name} data-testid="account" onClick={(e) => setMenu(menu ? null : e.currentTarget.getBoundingClientRect())}>{face}</button>
        </div>
      </nav>
      {more && (
        <div className="cx-more" role="dialog" aria-label="All tools" onClick={(e) => { if ((e.target as HTMLElement).closest('a')) setMore(false); }}>
          {rail.map((g) => (
            <section key={g.label ?? 'top'}>
              {g.label && <div className="cx-label">{g.label}</div>}
              {g.items.map((it) => (
                <Link key={it.name} to={it.to} search={it.search as never} className="cx-more-item" data-testid={`more-${it.icon}`}>
                  <ToolIcon name={it.icon} size={28} /><span><b>{it.name}</b><small>{it.blurb}</small></span>
                </Link>
              ))}
            </section>
          ))}
          <section>
            <button type="button" className="cx-more-item" onClick={(e) => { setMore(false); setMenu(e.currentTarget.getBoundingClientRect()); }}>
              <span className="cx-avatar" aria-hidden="true">{face}</span><span><b>{name || 'Account'}</b><small>Security, theme, organisation and sign out</small></span>
            </button>
          </section>
        </div>
      )}
      {card.node}
      <CommandPalette tools={rail.flatMap((g) => g.items).map((it) => ({ name: it.name, to: it.to, search: it.search, blurb: it.blurb }))} role={me.role} />
      {menu && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 55 }} onClick={() => setMenu(null)} />
          <div className="cx-menu" style={{ left: menu.right + 10, bottom: Math.max(10, window.innerHeight - menu.bottom) }} onClick={(e) => { if ((e.target as HTMLElement).closest('a,button[data-close]')) setMenu(null); }}>
            <div className="cx-menu-head"><b>{name}</b><span className="cx-label">{session.data?.user.email} · {me.role}</span></div>
            {['owner', 'admin', 'manager'].includes(me.role) && <Link to="/admin" className="cx-menu-item"><ToolIcon name="access" size={26} />Admin</Link>}
            <Link to="/security" className="cx-menu-item" data-testid="nav-security">Security</Link>
            {['owner', 'admin', 'manager'].includes(me.role) && <a href="/pocket" className="cx-menu-item" data-testid="nav-pocket">iPad view</a>}
            {(orgs.data?.length ?? 0) > 1 && <>
              <div className="cx-menu-sep" />
              <div className="cx-menu-item" style={{ cursor: 'default' }}><span className="cx-label">Organisation</span></div>
              {orgs.data!.map((o) => <button key={o.id} type="button" className="cx-menu-item" data-close data-testid="org-switch" aria-current={o.id === me.orgId ? 'true' : undefined}
                onClick={() => void switchOrg(o.id)} style={o.id === me.orgId ? { fontWeight: 600 } : undefined}>{o.id === me.orgId ? '✓ ' : ''}{o.name}</button>)}
            </>}
            <div className="cx-menu-sep" />
            <div className="cx-menu-item" style={{ cursor: 'default', justifyContent: 'space-between' }}><span className="cx-label">Theme</span><ThemeSwitch /></div>
            <div className="cx-menu-sep" />
            <button type="button" className="cx-menu-item" data-close onClick={signOut} data-testid="signout">Sign out</button>
          </div>
        </>
      )}
      <main className="cx-main"><Outlet /></main>
      <RunningBar />
    </div>
  );
}

function NotBuilt({ label }: { label: string }) {
  return (
    <section className="cx-panel page cx-rise">
      <div className="cx-eyebrow">Workspace · <b>{label}</b></div>
      <h1 className="cx-hero">{label} arrives with its migration wave.</h1>
      <p className="cx-body">This route is reserved by the platform skeleton. See the rebuild plan, Phase 3.</p>
    </section>
  );
}

const root = createRootRoute({ component: Outlet });

// Public: sign-in and the second step.
const signIn = createRoute({ getParentRoute: () => root, path: '/signin', component: SignIn });
const verify = createRoute({ getParentRoute: () => root, path: '/signin/verify', component: VerifySecondFactor });
const inviteRoute = createRoute({ getParentRoute: () => root, path: '/invite/$id', component: AcceptInvite });
// Public customer pages: no sign-in, the link is the credential (Wave A4b).
const portalRoute = createRoute({ getParentRoute: () => root, path: '/help/portal/$token', component: Portal });
const csatRoute = createRoute({ getParentRoute: () => root, path: '/help/csat/$token', component: Csat });
const helpRoute = createRoute({ getParentRoute: () => root, path: '/help/$org/$desk', component: HelpCentre });
// Public booking pages (Wave A5a).
const manageRoute = createRoute({ getParentRoute: () => root, path: '/book/manage/$token', component: ManageBooking });
const bookPageRoute = createRoute({ getParentRoute: () => root, path: '/book/$org/$page', component: BookingPage });
const bookTypeRoute = createRoute({ getParentRoute: () => root, path: '/book/$org/$page/$type', component: BookingType });
// Old MeetPoint links keep working: /schedule/<business>[/<type>] -> /book/<org>/<business>[/<type>].
const schedulePageRoute = createRoute({ getParentRoute: () => root, path: '/schedule/$page', component: ScheduleRedirect });
const scheduleTypeRoute = createRoute({ getParentRoute: () => root, path: '/schedule/$page/$type', component: ScheduleRedirect });
// Public onboarding pages (Wave B2): the client's intake form and one-time credential link. Brand-named, never CAIOS.
const onboardSecretRoute = createRoute({ getParentRoute: () => root, path: '/onboard/secret/$token', component: OnboardSecret });
const onboardRoute = createRoute({ getParentRoute: () => root, path: '/onboard/$token', component: OnboardIntake });
// The owner's iPad view: its own lock (a passkey sign-in), so it sits outside the app gate.
const pocketRoute = createRoute({ getParentRoute: () => root, path: '/pocket', component: Pocket });
// Signed in, but must finish security setup first.
const setup = createRoute({
  getParentRoute: () => root, path: '/security/setup', component: SecuritySetup,
  beforeLoad: async () => {
    const g = await gate();
    if (g.kind === 'signin') throw redirect({ to: '/signin' });
    if (g.kind === 'verify') throw redirect({ to: '/signin/verify' });
    if (g.kind === 'ok') throw redirect({ to: '/' });
    return { recovery: g.kind === 'recover' };
  },
});
// Everything else: the app shell, only for a fully signed-in person with an org.
const app = createRoute({
  getParentRoute: () => root, id: '_app', component: Shell,
  beforeLoad: async () => {
    const g = await gate();
    if (g.kind === 'signin' || g.kind === 'no-org') throw redirect({ to: '/signin' });
    if (g.kind === 'enrol' || g.kind === 'recover') throw redirect({ to: '/security/setup' });
    if (g.kind === 'verify') throw redirect({ to: '/signin/verify' });
    return { me: g.me };
  },
});
const securityRoute = createRoute({ getParentRoute: () => app, path: '/security', component: Security });
const dealsRoute = createRoute({ getParentRoute: () => app, path: '/sales/deals', component: Deals });
const callsRoute = createRoute({ getParentRoute: () => app, path: '/sales/calls', component: Calls });
const outreachRoute = createRoute({ getParentRoute: () => app, path: '/sales/outreach', component: Outreach });
const relationshipsRoute = createRoute({ getParentRoute: () => app, path: '/sales/relationships', component: Relationships });
const goalsRoute = createRoute({ getParentRoute: () => app, path: '/goals', component: GoalsPage });
const referralsRoute = createRoute({ getParentRoute: () => app, path: '/finance/referrals', component: Referrals });
/** Harbor's pages share one rail entry; each page lists the others in its side panel (delivery-nav.tsx). */
const delivery = createRoute({ getParentRoute: () => app, id: '_delivery', component: Outlet });
const connectionsRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/connections', component: Connections });
const proofboardRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/proofboard', component: Proofboard });
const reportsRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/reports', component: Reports });
const contentRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/content', component: Content });
const searchRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/search', component: Search });
const socialRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/social', component: Social });
const adsRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/ads', component: Ads });
const resultsRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/results', component: Results });
const approveRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/approve', component: Recommendations });
const agentsRoute = createRoute({ getParentRoute: () => delivery, path: '/delivery/agents', component: Agents });
const designRoute = createRoute({ getParentRoute: () => app, path: '/delivery/design', component: DesignLibrary });
const mediaRoute = createRoute({ getParentRoute: () => app, path: '/comms/media', component: Media });
const clientRoute = createRoute({ getParentRoute: () => app, path: '/clients/$id', component: ClientDetail });
const workspaceRoutes = WORKSPACES.map((w) =>
  createRoute({ getParentRoute: () => app, path: w.path, component: w.path === '/' ? Home : w.path === '/admin' ? Admin : w.path === '/ops' ? Operations : w.path === '/support' ? Support : w.path === '/scheduling' ? Scheduling : w.path === '/clients' ? Clients : w.path === '/onboarding' ? Onboarding : w.path === '/finance' ? Finance : w.path === '/sales' ? Leads : w.path === '/comms' ? Comms : w.path === '/delivery' ? () => <Navigate to="/delivery/reports" replace /> : () => <NotBuilt label={(w as { label: string }).label} /> }));

function NotFound() {
  return (
    <div className="cx-app auth-screen">
      <section className="cx-hero-panel auth-card cx-rise">
        <div className="cx-eyebrow"><b>Not found</b> — nothing lives at this address</div>
        <h1 className="cx-hero">This page doesn’t exist</h1>
        <p className="cx-body">The link may be old or mistyped.</p>
        <Link to="/" className="cx-btn cx-btn--primary" style={{ justifySelf: 'start' }}>Go to The Bridge</Link>
      </section>
    </div>
  );
}

export const router = createRouter({ routeTree: root.addChildren([signIn, verify, inviteRoute, portalRoute, csatRoute, helpRoute, manageRoute, bookPageRoute, bookTypeRoute, schedulePageRoute, scheduleTypeRoute, onboardSecretRoute, onboardRoute, pocketRoute, setup, app.addChildren([...workspaceRoutes, securityRoute, clientRoute, dealsRoute, callsRoute, outreachRoute, relationshipsRoute, goalsRoute, referralsRoute, delivery.addChildren([connectionsRoute, proofboardRoute, reportsRoute, contentRoute, searchRoute, socialRoute, adsRoute, resultsRoute, approveRoute, agentsRoute]), designRoute, mediaRoute])]), defaultNotFoundComponent: NotFound });
declare module '@tanstack/react-router' { interface Register { router: typeof router } }
